/*
 * Copyright (c) 2025 Amazon.com, Inc. or its affiliates. All rights reserved.
 *
 * PROPRIETARY/CONFIDENTIAL. USE IS SUBJECT TO LICENSE TERMS.
 */

import {useState, useCallback} from 'react';
import {isDevelopmentStage} from './environment';

export interface DisplayError {
  id: string;
  error: Error;
  timestamp: number;
  context?: string;
}

/**
 * Hook for managing error display in development stages.
 * In alpha/beta stages, errors are shown prominently in the UI.
 * In production stages, errors are only logged to console.
 */
export const useErrorDisplay = () => {
  const [errors, setErrors] = useState<DisplayError[]>([]);

  const displayError = useCallback((error: Error, context?: string) => {
    // Always log to console regardless of stage
    console.error(`Error${context ? ` in ${context}` : ''}:`, error);

    // Only show UI display in development stages
    if (isDevelopmentStage()) {
      const displayError: DisplayError = {
        id: `${Date.now()}-${Math.random()}`,
        error,
        timestamp: Date.now(),
        context,
      };

      setErrors(prev => [...prev, displayError]);
    }
  }, []);

  const dismissError = useCallback((errorId: string) => {
    setErrors(prev => prev.filter(e => e.id !== errorId));
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors([]);
  }, []);

  return {
    errors,
    displayError,
    dismissError,
    clearAllErrors,
    hasErrors: errors.length > 0,
    isDevStage: isDevelopmentStage(),
  };
};
